// Pushes today's nutrition stats to the iOS home-screen widgets via the
// native SolanoWidget Capacitor plugin. No-op on web / older app binaries.
import { Capacitor, registerPlugin } from "@capacitor/core";
import { api, API, getAuthToken } from "@/lib/http";

const SolanoWidget = registerPlugin("SolanoWidget");

// Stores the auth token + API base in the iOS App Group so the widget's
// TimelineProvider can fetch stats itself (every ~30 min, app closed or not).
export async function configureWidgetAuth() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await SolanoWidget.configure({ token: getAuthToken() || "", apiBaseUrl: API });
  } catch {
    // old binary without configure() — ignore
  }
}

// Logout: wipe credentials + cached stats from the App Group.
export async function clearWidgetAuth() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await SolanoWidget.configure({ token: "", apiBaseUrl: "" });
  } catch {}
}

// Fetches all widget numbers from the API and pushes them to the widget.
// Called when the app goes to background and after logging meals/water.
// Throttled: at most one server fetch every 10 s (the native side already
// skips the timeline reload when values are unchanged).
export async function refreshWidgetFromServer() {
  if (!Capacitor.isNativePlatform()) return;
  const now = Date.now();
  const wait = 10000 - (now - refreshWidgetFromServer._last);
  if (wait > 0) {
    // Trailing call so the newest values still reach the widget.
    if (!refreshWidgetFromServer._pending) {
      refreshWidgetFromServer._pending = setTimeout(() => {
        refreshWidgetFromServer._pending = null;
        refreshWidgetFromServer();
      }, wait + 50);
    }
    return;
  }
  refreshWidgetFromServer._last = now;
  try {
    await configureWidgetAuth();
    const { data } = await api.get("/client/widget-stats");
    await SolanoWidget.updateStats(data);
  } catch {
    // logged out / offline / old binary — widget keeps last state
  }
}
refreshWidgetFromServer._last = 0;

// Diagnostics button: bypasses the throttle, returns the synced values,
// throws on failure so the UI can show a precise error.
export async function forceWidgetSync() {
  const { data } = await api.get("/client/widget-stats");
  if (Capacitor.isNativePlatform()) {
    await configureWidgetAuth();
    await SolanoWidget.updateStats(data);
  }
  refreshWidgetFromServer._last = Date.now();
  return data;
}

export async function syncWidgetStats(stats) {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await SolanoWidget.updateStats(stats);
  } catch {
    // Plugin missing on old binaries — ignore silently.
  }
}

// Water queued via the widget's +250/+500 buttons while the app was closed.
export async function drainPendingWidgetWater() {
  if (!Capacitor.isNativePlatform()) return 0;
  try {
    const { ml } = await SolanoWidget.drainPendingWater();
    return Number(ml) || 0;
  } catch {
    return 0;
  }
}
